<template>
  <!-- Removed @dragover and @drop from the root div -->
  <InteractiveModal
    modalId="enablerModal"
    :modalTitle="isNew ? 'Create Enabler' : 'Edit Enabler'"
    :modalContent="displayModalContent"
    :isModalActive="isModalActive"
    :isMinimized="isMinimized"
    :draggable="draggable"
    :isDirty="isDirty"
    @close="$emit('close')"
    @minimize="$emit('minimize')"
    @restore="$emit('restore')"
  >
    <!-- Use named slots for modal-body and modal-footer -->
    <template slot="modal-body">
      <b-field
        label="Title"
        :message="errorMessage.title"
        :type="errorMessage.title ? 'is-danger' : undefined"
      >
        <b-input
          v-model="dirtyItem.title"
          type="textarea"
          @input="validateData()"
        />
      </b-field>
      <b-field label="Domain/Category">
        <DomainCategoryDropdown
          v-if="!isDomainCategoriesLoading"
          modalTitle="Enabler"
          :isNew="isNew"
          :projectId="projectId"
          :gripDomainCategories="gripDomainCategoryOptions"
          :gripDomainCategoryDetails="gripDomainCategoryDetails"
          @selectedGripDomainCategory="handleSelectedGripDomainCategory"
          @close="$emit('close')"
        />
      </b-field>
      <div>
        <b-field
          label="Description"
          :message="errorMessage.description"
          :type="errorMessage.description ? 'is-danger' : undefined"
          class="width-full display-block-important"
        >
          <b-input
            v-model="dirtyItem.description"
            type="textarea"
            @input="validateData()"
          />
          <span
            :class="[$style.descriptionWordCount, 'align-end', 'display-flex']"
            >{{ wordCount(dirtyItem.description) }}/150 words</span
          >
        </b-field>
      </div>
      <b-field label="Link(s)" class="width-full">
        <div>
          <div v-for="(link, linkIndex) in itemLinks" :key="linkIndex">
            <div
              :class="[$style.linkContainer, 'display-flex', 'margin-top16']"
            >
              <b-field
                class="width-100 no-bottom-margin"
                :message="link.errorMessage"
                :type="!link.isValid ? 'is-danger' : undefined"
              >
                <b-input
                  v-model="link.url"
                  :class="[
                    $style.linkInput,
                    'width-100',
                    'bg-white',
                    'align-right',
                  ]"
                  type="text"
                  @input="validateGripLink(linkIndex)"
                >
                </b-input>
              </b-field>
              <a
                :class="[
                  $style.deleteLinkColor,
                  'padding-top12',
                  'margin-left16',
                  'cursor-pointer',
                ]"
                @click="removeLinkTextInput(linkIndex)"
              >
                <b-icon icon="delete" size="is-small"></b-icon>
              </a>
            </div>
            <div v-if="link.url && link.isValid">
              <a target="_blank" :class="$style.sourceLink" :href="sourceLink">
                Go to source link
                <b-icon icon="launch" size="is-small"></b-icon>
              </a>
            </div>
          </div>
          <a
            class="add-participant-text cursor-pointer"
            @click="addLinkTextInput"
          >
            + Add Link
          </a>
        </div>
      </b-field>
      <b-field label="Attachment(s)">
        <GenericFileUploader :fileAttachment="fileAttachment" />
      </b-field>
    </template>
    <template slot="modal-footer">
      <button
        v-if="gripEnablerDetails && Object.keys(gripEnablerDetails).length > 0"
        class="button is-primary is-rounded"
        @click="onUpdateGripEnabler()"
      >
        Save
      </button>
      <button
        v-else
        class="button is-primary is-rounded"
        @click="onAddGripEnabler()"
      >
        Create Enabler
      </button>
    </template>
  </InteractiveModal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { Action, State } from 'vuex-class';
import GenericFileUploader from '../../../components/uploader/GenericFileUploader.vue';
import { isTruthy } from '../../../jbi-shared/util/watcher.vue-decorator';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  CreateGripEnablerBase,
  FileAttachmentDTO,
  GripEnablerDTO,
  GripDomainCategory,
  LinkDTO,
  UpdateGripEnablerPayload,
} from '../../../store/types/grip.types';
import { ApiState } from '../../../store/types/general.types';
import DomainCategoryDropdown from '../DomainCategory/DomainCategoryDropdown.vue';
import {
  getSourceLink,
  getWordCount,
  validateLink,
} from '../../../utils/general.util';
import InteractiveModal from '@/components/base/InteractiveModal.vue';
import GripModalMixin from '@/views/Grip/components/grip-modal.mixin';

@Component({
  components: { DomainCategoryDropdown, GenericFileUploader, InteractiveModal },
})
export default class EnablerModal extends mixins(GripModalMixin) {
  @Prop() public gripEnablerDetails!: GripEnablerDTO;

  get gripDomainCategoryDetails(): GripDomainCategory | null {
    return this.gripEnablerDetails?.gripDomainCategory ?? null;
  }

  public dirtyItem: CreateGripEnablerBase = {
    title: '',
    description: '',
    links: [],
    attachments: [],
    gripDomainCategoryId: null,
  };
  public itemLinks: LinkDTO[] = [];
  public errorMessage: {
    title: string | null;
    description: string | null;
  } = { title: null, description: null };

  public validateData() {
    if (!this.dirtyItem.title.trim().length) {
      this.errorMessage.title = 'This Field is Required';
    } else {
      this.errorMessage.title = null;
    }

    if (
      this.dirtyItem.description &&
      this.wordCount(this.dirtyItem.description.trim()) > 150
    ) {
      this.errorMessage.description = 'Max 150 words are allowed.';
    } else {
      this.errorMessage.description = null;
    }

    return this.errorMessage;
  }

  public onAddGripEnabler(): void {
    this.validateData();
    if (
      !this.errorMessage.title &&
      !this.errorMessage.description &&
      this.areLinksValid
    ) {
      this.getGripItemLinks();
      this.getItemAttachments();
      this.dirtyItem.gripDomainCategoryId = this.selectedGripDomainCategoryId;
      this.$emit('create', this.dirtyItem);
    }
  }

  public onUpdateGripEnabler(): void {
    this.getGripItemLinks();
    this.getItemAttachments();
    this.dirtyItem.gripDomainCategoryId = this.selectedGripDomainCategoryId;
    const enablerChanges: { [key: string]: any } = {};
    const currentEnablerData: { [key: string]: any } = {
      ...this.dirtyItem,
    };
    const originalEnablerDetails: { [key: string]: any } = {
      ...this.gripEnablerDetails,
    };
    Object.keys(currentEnablerData).forEach((key: string) => {
      if (key === 'gripDomainCategoryId') {
        const originalDomainCategoryId = originalEnablerDetails.gripDomainCategory
          ? originalEnablerDetails.gripDomainCategory.id
          : null;
        if (
          JSON.stringify(currentEnablerData.gripDomainCategoryId) !==
          JSON.stringify(originalDomainCategoryId)
        ) {
          enablerChanges.gripDomainCategoryId =
            currentEnablerData.gripDomainCategoryId;
        }
      } else {
        // Compare with originalEnablerDetails, not enablerInfo (which was a copy of gripEnablerDetails)
        if (
          JSON.stringify(currentEnablerData[key]) !==
          JSON.stringify(originalEnablerDetails[key])
        ) {
          enablerChanges[key] = currentEnablerData[key];
        }
      }
    });

    this.validateData();
    if (
      !this.errorMessage.title &&
      !this.errorMessage.description &&
      this.areLinksValid
    ) {
      // Always emit 'update' if validation passes.
      // EnablerSettings will check if Object.keys(enablerChanges).length > 0
      this.$emit('update', {
        id: this.gripEnablerDetails.id,
        ...enablerChanges, // This will be an empty object if no actual data changed
      });
    }
  }

  public mounted(): void {
    this.isDomainCategoriesLoading = true;
    this.getGripDomainCategories(this.projectId);
    if (
      this.gripEnablerDetails &&
      Object.keys(this.gripEnablerDetails).length > 0
    ) {
      const {
        gripDomainCategory,
        id,
        ...enablerInfo
      } = this.gripEnablerDetails;
      enablerInfo.links.forEach((link: string) => {
        this.count++;
        this.itemLinks.push({
          url: link,
          count: this.count,
          isValid: true,
          errorMessage: '',
        });
      });

      enablerInfo.attachments.forEach((file) => {
        const fileData = file.split('/');
        this.fileAttachment.push({
          type: file,
          name: fileData[fileData.length - 1],
        });
      });

      this.selectedGripDomainCategoryId = gripDomainCategory
        ? gripDomainCategory.id
        : null;

      this.dirtyItem = {
        ...enablerInfo,
        gripDomainCategoryId: this.selectedGripDomainCategoryId,
      };
    }

    this.setInitialFormState();
  }
}
</script>
<style lang="scss" module>
.descriptionWordCount {
  color: $grey;
  font-size: $size-5;
  font-weight: $weight-regular;
  flex-direction: column-reverse; /* This specific flex property remains */
}

.linkInput {
  height: 44px;
  border-color: $grey-border-2 !important;
  color: $dark !important;
}

.deleteLinkColor {
  color: $secondary;
}

.sourceLink {
  color: $secondary;
  font-size: $size-6;
  font-weight: $weight-bold;
}
</style>
